// The "Billed From" block of the invoices the web renders itself (the admin's
// purchase modals, a member's legacy membership receipt). It follows the
// server's invoice (packages/api/src/services/commerce/invoice.ts), which is the
// document of record:
//   - the address: a Tax Invoice's registered address (its GST registration's),
//     else the gym's business address (gym.registeredAddress), else the gym's
//     physical address;
//   - the branch the sale was made at (snapshotted on the sale), beside it when
//     it is somewhere else;
//   - a Tax Invoice's legal name, when it differs from the trade name.
// Nothing configured renders nothing, never a placeholder.

export interface SellerRegistration {
  gstin?: string | null;
  legalName?: string | null;
  registeredAddress?: string | null;
}

export interface BilledFromSource {
  branch?: {
    addressLine?: string | null;
    city?: string | null;
    pincode?: string | null;
  } | null;
  gym?: {
    registeredAddress?: string | null;
    address?: string | null;
    /** The gym's registrations, to find a legacy sale's by its GSTIN. */
    gstRegistrations?: SellerRegistration[] | null;
  } | null;
  /** The registration the sale was made under (sales through the sale core). */
  gstRegistration?: SellerRegistration | null;
  invoiceGstin?: string | null;
  documentType?: string | null;
}

const branchAddress = (b: BilledFromSource['branch']) => {
  if (!b) return '';
  const cityLine = [b.city, b.pincode].map(s => (s || '').trim()).filter(Boolean).join(' - ');
  return [(b.addressLine || '').trim(), cityLine].filter(Boolean).join(', ');
};

/**
 * The registration a Tax Invoice was issued under: the sale's own, else (a
 * legacy sale) the gym's registration carrying the invoice's GSTIN. A Bill of
 * Supply has none.
 */
export function invoiceRegistration(source: BilledFromSource | null | undefined): SellerRegistration | null {
  if (!source || source.documentType === 'bill_of_supply') return null;
  if (source.gstRegistration) return source.gstRegistration;
  const gstin = source.invoiceGstin;
  return (gstin && source.gym?.gstRegistrations?.find(r => r.gstin === gstin)) || null;
}

export interface BilledFrom {
  /** The registration's legal name, when it differs from the name shown above it. */
  legalName: string | null;
  address: string;
  /** Where it was sold, shown beside a registered address that is elsewhere. */
  branchAddress: string | null;
}

/** The seller block under the gym's trade name. */
export function resolveBilledFrom(source: BilledFromSource | null | undefined, tradeName: string): BilledFrom {
  const reg = invoiceRegistration(source);
  const legal = reg?.legalName?.trim();
  const legalName = legal && legal !== tradeName.trim() ? legal : null;
  const address = (reg?.registeredAddress || source?.gym?.registeredAddress || source?.gym?.address || '').trim();
  const branch = branchAddress(source?.branch);
  // With no address of its own, the branch's is the address.
  if (!address) return { legalName, address: branch, branchAddress: null };
  return { legalName, address, branchAddress: branch && branch !== address ? branch : null };
}
